import type { ReactNode } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { CARD_SHADOW, COLORS, DRAFT_PILL, PLAN_CHIP, STATUS_PILL, TREND_STYLES, WARNING_CHIP, type TrendCategory } from './tokens';

const TREND_ICONS: Record<TrendCategory, typeof TrendingUpRoundedIcon> = {
  rising: TrendingUpRoundedIcon,
  falling: TrendingDownRoundedIcon,
  met: CheckRoundedIcon,
  flat: RemoveRoundedIcon,
  na: RemoveRoundedIcon,
};

export const TILE_MIN_WIDTH = 160;

export const TILE_VALUE_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'wrap',
  rowGap: 0.5,
} as const;

export function Pill({ label, bg, fg }: { label: string; bg: string; fg: string }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        bgcolor: bg,
        color: fg,
        px: 1.25,
        py: 0.375,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

export function ConfidencePill({ label }: { label: string }) {
  return <Pill label={label} bg={DRAFT_PILL.bg} fg={DRAFT_PILL.fg} />;
}

export type PlanChipTone = keyof typeof PLAN_CHIP;

export function PlanChip({ label, tone = 'grey' }: { label: string; tone?: PlanChipTone }) {
  return <Pill label={label} bg={PLAN_CHIP[tone].bg} fg={PLAN_CHIP[tone].fg} />;
}

export function StatusPill({ label }: { label: string }) {
  return <Pill label={label} bg={STATUS_PILL.bg} fg={STATUS_PILL.fg} />;
}

export function BelowExitPill() {
  return <Pill label="Below exit" bg={WARNING_CHIP.bg} fg={WARNING_CHIP.fg} />;
}

export function PlanCard({
  title,
  subtitle,
  right,
  children,
  sx,
}: {
  title?: string;
  subtitle?: string;
  right?: ReactNode;
  children: ReactNode;
  sx?: object;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: CARD_SHADOW,
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        ...sx,
      }}
    >
      {(title || right) && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            {title && (
              <Typography sx={{ fontSize: 24, fontWeight: 600, color: COLORS.ink, lineHeight: '32px' }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography sx={{ fontSize: 16, fontWeight: 500, color: COLORS.muted, mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {right}
        </Box>
      )}
      {children}
    </Paper>
  );
}

export function SectionLabel({ children, color }: { children: ReactNode; color?: string }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: color ?? COLORS.muted,
      }}
    >
      {children}
    </Typography>
  );
}

export function TrendPill({ category, label }: { category: TrendCategory; label: string }) {
  const s = TREND_STYLES[category];
  const Icon = TREND_ICONS[category];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: s.bg,
        color: s.fg,
        pl: 1,
        pr: 1.5,
        py: 0.5,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon sx={{ fontSize: 16 }} />
      {label}
    </Box>
  );
}

export function StatTile({
  label,
  value,
  valueSuffix,
  caption,
  trend,
  flag,
  emphasize,
}: {
  label: string;
  value: string;
  valueSuffix?: string;
  caption?: string;
  trend?: { category: TrendCategory; label: string };
  flag?: ReactNode;
  emphasize?: boolean;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: TILE_MIN_WIDTH,
        bgcolor: COLORS.bg,
        borderRadius: 2,
        px: 2,
        py: 1.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <SectionLabel>{label}</SectionLabel>
      <Box sx={TILE_VALUE_ROW_SX}>
        <Typography
          sx={{
            fontSize: emphasize ? 36 : 30,
            fontWeight: 800,
            letterSpacing: '-0.01em',
            color: COLORS.ink,
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>
        {valueSuffix && (
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>{valueSuffix}</Typography>
        )}
        {trend && <TrendPill category={trend.category} label={trend.label} />}
        {flag}
      </Box>
      {caption && (
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: COLORS.muted }}>{caption}</Typography>
      )}
    </Box>
  );
}

export function MetaField({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
      <SectionLabel>{label}</SectionLabel>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink, whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Box>
  );
}

export function Bullet({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box
        sx={{
          mt: '10px',
          flexShrink: 0,
          width: 4,
          height: 4,
          borderRadius: 999,
          bgcolor: COLORS.navy,
        }}
      />
      <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: 1.5, color: COLORS.ink }}>
        {children}
      </Typography>
    </Box>
  );
}
